import axios, { Axios, CancelTokenSource, AxiosInstance, CancelToken, AxiosProxyConfig } from 'axios';

import * as SMH from '../../typings';
import DAOGenerator, { DAOConfig, APIPlugin, ErrorHandler, CustomAttributes } from '../dao-generator';
import { snake2camelPlugin, camel2snakePlugin } from '../dao-generator/plugins';
import EventBus from '../event';
import commonAPIConfig from './api/common.api';
import userAPIConfig from './api/user.api';
import syncAPIConfig from './api/sync.api';
import getLoginAPI from './api/login.api';
import getHttp, { EnvType, DEFAULT_BASE_URL, DEFAULT_ENV_TYPE } from '../http';

type GetUserInfo = () => Promise<SMH.UserInfo>;

type GetSpaceInfo = (spaceId?: SMH.SpaceId, isFetch?: boolean, spaceOrgId?: number) => Promise<SMH.SpaceToken>;

type GetShareInfo = (shareToken: string, isFetch?: boolean) => Promise<any>;
type GetCollectInfo = (collectToken: string, isFetch?: boolean) => Promise<any>;

type GetSyncInfo = (syncId: SMH.SyncId, isFetch?: boolean) => Promise<any>;

interface GetInfoFunctionConfig {
  getUserInfo?: GetUserInfo;
  getSpaceInfo?: GetSpaceInfo;
  getShareInfo?: GetShareInfo;
  getCollectInfo?: GetCollectInfo;
  getSyncInfo?: GetSyncInfo;
}

interface SMHConfig extends GetInfoFunctionConfig, DAOConfig {
  envType?: EnvType;
  baseURL?: string;
  cancelToken?: CancelToken;
  proxy?: AxiosProxyConfig | false;
  maxRedirects?: number;
}

class SMHSDK extends EventBus {
  public api: any;

  public userAPI: SMH.UserRequestSet = {} as SMH.UserRequestSet;

  public commonAPI: SMH.CommonRequestSet = {} as SMH.CommonRequestSet;

  public syncAPI: SMH.SyncRequestSet = {} as SMH.SyncRequestSet;

  private getUserInfo: GetUserInfo | null = null;

  private getSpaceInfo: GetSpaceInfo | null = null;

  private getSyncInfo: GetSyncInfo | null = null;

  private getShareInfo: GetShareInfo | null = null;

  private getCollectInfo: GetCollectInfo | null = null;

  private dao!: DAOGenerator;

  private http!: AxiosInstance;

  public request!: Axios['request'];

  /**
   * 默认是不增加代理的
   * @private
   */
  private config: SMHConfig = { proxy: false };

  constructor(config: SMHConfig) {
    super();
    this.changeConfig(config);
  }

  public static gererateCancelSource(): CancelTokenSource {
    return axios.CancelToken.source();
  }

  /**
   * 单独自改smh的代理配置
   * @param proxyConfig
   */
  public changeProxy(proxyConfig: AxiosProxyConfig | false) {
    // 如果是false 就恢复默认值
    if (proxyConfig === false) {
      const http = (window.parent as any).require('http');
      const https = (window.parent as any).require('https');
      this.http.defaults.httpsAgent = new https.Agent({ keepAlive: true });
      this.http.defaults.httpAgent = new http.Agent({ keepAlive: true });
      return;
    }
    const { httpsOverHttp, httpOverHttp } = (window.parent as any).require('tunnel-agent');
    const proxy = { host: proxyConfig.host, port: proxyConfig.port };
    this.http.defaults.httpsAgent = httpsOverHttp({ proxy });
    this.http.defaults.httpAgent = httpOverHttp({ proxy });
  }
  /**
   * @description 修改 SDK 配置
   * @param {Partial<SMHConfig>} config
   * @memberof SMHSDK
   */
  public changeConfig(config: Partial<SMHConfig>) {
    this.config = { ...this.config, ...config };
    this.http =
      config.http ||
      getHttp({
        baseURL: this.config.baseURL || DEFAULT_BASE_URL,
        envType: this.config.envType || DEFAULT_ENV_TYPE,
        eventBus: this,
        cancelToken: this.config.cancelToken || null,
      });
    this.request = this.http.request;
    // 若有配置代理，则设置一遍代理
    if (this.config.envType === 'node') {
      config.maxRedirects = 0;
      if (config.proxy) {
        this.changeProxy(config.proxy);
      }
    }
    // 默认预制插件
    const { plugins = [] } = this.config;
    plugins.unshift(snake2camelPlugin, this.getAutoParamsPlugin);
    plugins.push(camel2snakePlugin);
    this.dao = new DAOGenerator({
      plugins,
      http: this.http,
      errorHandler: this.errorHandler,
      customAttributesKeys: ['spaceId', 'shareToken', 'collectToken', 'syncId'],
    });
    // 挂载 API
    this.api = getLoginAPI(this.http, this.config.baseURL);
    this.commonAPI = this.dao.mountAPISet('commonAPI', commonAPIConfig, this.config.baseURL);
    this.userAPI = this.dao.mountAPISet('userAPI', userAPIConfig, this.config.baseURL);
    this.syncAPI = this.dao.mountAPISet('syncAPI', syncAPIConfig, this.config.baseURL);
    this.initGetInfoFunction(this.config);
  }

  public initGetInfoFunction(parmas: GetInfoFunctionConfig): void {
    const { getUserInfo, getSpaceInfo, getShareInfo, getCollectInfo, getSyncInfo } = parmas;
    getUserInfo && (this.getUserInfo = getUserInfo);
    getSpaceInfo && (this.getSpaceInfo = getSpaceInfo);
    getShareInfo && (this.getShareInfo = getShareInfo);
    getCollectInfo && (this.getCollectInfo = getCollectInfo);
    getSyncInfo && (this.getSyncInfo = getSyncInfo);
  }

  private errorHandler: ErrorHandler = async (error, data) => {
    const { code } = error as CustomErrorData;
    const { config } = data;
    // 网络请求断开的错误是网关处报的错，不经过后端代码判断，故此处需要特殊处理
    if (code?.indexOf('getaddrinfo ENOTFOUND') > -1) {
      error.code = 'GetAddrInfoError';
    } else if (code === 'SpaceNotFound' && this.getSpaceInfo) {
      // 如果是个人空间缓存的spaceId已经失效导致出现该code，则需要强制刷新个人空间的spaceId缓存
      const { spaceId = '' } = config?.customAttributes as CustomAttributes;
      let spaceInfo: SMH.SpaceToken | undefined = undefined;
      try {
        spaceInfo = await this.getSpaceInfo();
      } catch (e) {}
      if (spaceInfo?.spaceId === spaceId) {
        const { accessToken: newAccessToken, spaceId: newSpaceId } = await this.getSpaceInfo('', true);
        if (newAccessToken) {
          config.params.access_token = newAccessToken;
          config.url = (config.url as string).replace(spaceId, newSpaceId);
          const response = await this.http(config);
          return response;
        }
      }
      return error;
    } else if (['AccessTokenNotMatchSpace', 'InvalidAccessToken'].includes(code) && config.params.access_token) {
      const {
        spaceId = '',
        shareToken = '',
        collectToken = '',
        syncId = 0,
      } = config?.customAttributes as CustomAttributes;
      let newAccessToken = '';

      if (shareToken && this.getShareInfo) {
        newAccessToken = (await this.getShareInfo(shareToken, true)).accessToken;
      } else if (collectToken && this.getCollectInfo) {
        newAccessToken = (await this.getCollectInfo(collectToken, true)).accessToken;
      } else if (this.getSyncInfo) {
        newAccessToken = (await this.getSyncInfo(syncId, true)).accessToken;
      } else if (this.getSpaceInfo) {
        newAccessToken = (await this.getSpaceInfo(spaceId, true)).accessToken;
      } else {
        this.emit('sign-out');
      }

      if (newAccessToken) {
        config.params.access_token = newAccessToken;
        const response = await this.http(config);
        return response;
      }
      this.emit('sign-out');
    }
    return error;
  };

  private getAutoParamsPlugin: APIPlugin = async (apiSetName, apiName, paramsObject) => {
    const { incomingParams, autoParams, defaultParams } = paramsObject;
    if (Object.keys(autoParams).length || incomingParams?.spaceId) {
      if (!this.getUserInfo) {
        Promise.reject(new Error('[SMH Warning] No getUserInfo function.'));
      } else {
        let spaceInfo: SMH.SpaceToken | undefined = undefined;
        try {
          if (this.getSpaceInfo) {
            spaceInfo = await this.getSpaceInfo();
          }
        } catch (e) {}
        // 只取出有用的字段
        const { userId, userToken, organizationId, libraryId } = await this.getUserInfo();
        const autoInfo = {
          userId,
          userToken,
          organizationId,
          libraryId,
          ...spaceInfo,
        };
        Object.keys(autoParams).forEach(key => {
          if (key in autoInfo) {
            // @ts-ignore
            autoParams[key] = autoInfo[key];
          }
        });
      }
    }

    if ('accessToken' in autoParams && incomingParams?.shareToken && this.getShareInfo) {
      const { accessToken, libraryId, spaceId, userId } = await this.getShareInfo(incomingParams.shareToken);
      autoParams.accessToken = accessToken;
      autoParams.libraryId = libraryId;
      autoParams.spaceId = spaceId;
      autoParams.userId = userId;
    }
    // 若 autoParams 存在 accessToken
    // 且 incomingParams 中有collectToken
    // 就 补充collectAccessToken
    else if ('accessToken' in autoParams && incomingParams?.collectToken && this.getCollectInfo) {
      const { accessToken, libraryId, spaceId, userId } = await this.getCollectInfo(incomingParams.collectToken);
      autoParams.accessToken = accessToken;
      autoParams.libraryId = libraryId;
      // 下载的时候会补spaceId，不需要替换成收集任务的spaceId
      autoParams.spaceId = incomingParams.spaceId || spaceId;
      autoParams.userId = userId;
    } else if ('accessToken' in autoParams && incomingParams?.syncId && this.getSyncInfo) {
      const { accessToken, libraryId, spaceId, inode } = await this.getSyncInfo(incomingParams.syncId);
      autoParams.accessToken = accessToken;
      autoParams.libraryId = libraryId;
      autoParams.spaceId = spaceId;
      if (!incomingParams.dirNode) {
        autoParams.dirNode = inode;
      }
    } else if ('accessToken' in autoParams && 'spaceId' in incomingParams && this.getSpaceInfo) {
      let spaceOrgId = autoParams.organizationId;
      if (incomingParams.spaceOrgId) {
        // TODO: 此处先如此处理，后续优化。处理原因：当前在A企业，但访问B企业的群组，因此需要外传orgId来取B企业信息
        spaceOrgId = incomingParams.spaceOrgId;
      }
      const spaceInfo = await this.getSpaceInfo(incomingParams.spaceId, false, spaceOrgId as number);
      if (apiSetName === 'commonAPI') {
        autoParams.organizationId = incomingParams.spaceOrgId;
      }
      autoParams.spaceOrgId = incomingParams?.spaceOrgId || spaceInfo?.spaceOrgId || autoParams.organizationId;
      autoParams.accessToken = spaceInfo.accessToken;
      autoParams.libraryId = spaceInfo.libraryId;
    } else if (incomingParams && autoParams && !incomingParams.spaceId) {
      incomingParams.spaceId = autoParams.spaceId;
    }

    Object.keys(autoParams).forEach(key => {
      if (typeof autoParams[key] === 'undefined') {
        delete autoParams[key];
      } else if (key in incomingParams && !incomingParams[key]) {
        incomingParams[key] = autoParams[key];
      }
    });
    return paramsObject;
  };
}

export default SMHSDK;
